import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Users, Phone, CalendarDays, Wallet, Camera, Pencil, Eye } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import { format } from 'date-fns';
import StaffTabs from './StaffTabs';
import { Button, Badge, Card, EmptyState, AttentionStrip, Spinner } from '../../components/ui';

const ROLE_LABELS = {
  MANAGER: 'Manager',
  OPERATOR: 'Operator',
  GROUNDSMAN: 'Ground keeper',
  CLEANER: 'Cleaner',
  PITCH_CURATOR: 'Pitch curator',
  ELECTRICIAN: 'Electrician',
};

export default function StaffList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchList = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await staffAPI.list({ search: search || undefined, role: roleFilter || undefined, status: statusFilter || undefined });
      setList(res.data || []);
    } catch (e) {
      console.error(e);
      setList([]);
      setErrorMsg(e?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever filters change OR when navigating back to this page
  useEffect(() => {
    fetchList();
  }, [roleFilter, statusFilter]);

  const onSearch = () => fetchList();

  const toggleStatus = async (row) => {
    const newStatus = (row.status || '').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    if (!window.confirm(`${newStatus === 'INACTIVE' ? 'Deactivate' : 'Reactivate'} ${row.name}?`)) return;
    try {
      await staffAPI.update(row.id, { status: newStatus });
      fetchList();
    } catch (e) {
      alert(e?.message || 'Failed to update status');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StaffTabs active="team" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Users className="h-7 w-7 text-ga-primary" />
              Staff Management
            </h1>
            <p className="text-ga-text-muted mt-1">Manage ground staff and roles</p>
          </div>
          <Button as={Link} to="/dashboard/staff/new">
            <Plus className="h-5 w-5" />
            Add Staff
          </Button>
        </div>

        <AttentionStrip
          tone="info"
          icon={Camera}
          title="Selfie check-in available"
          message="Staff can mark attendance with a selfie from their phone."
          action={(
            <Button as={Link} to="/dashboard/selfie-attendance" variant="secondary" className="px-3 py-1.5 text-xs">
              Open selfie attendance
            </Button>
          )}
        />

        <Card padding="sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <label htmlFor="staff-search" className="sr-only">Search staff</label>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ga-text-muted" aria-hidden />
              <input
                id="staff-search"
                type="text"
                placeholder="Search by name, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                className="ga-input w-full pl-10 pr-4 py-2 rounded-ga-md"
              />
            </div>
            <label htmlFor="staff-role-filter" className="sr-only">Filter by role</label>
            <select
              id="staff-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="ga-input w-full sm:w-40 py-2 rounded-ga-md"
            >
              <option value="">All roles</option>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <label htmlFor="staff-status-filter" className="sr-only">Filter by status</label>
            <select
              id="staff-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="ga-input w-full sm:w-36 py-2 rounded-ga-md"
            >
              <option value="">All status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
            <Button onClick={onSearch} variant="secondary">Search</Button>
          </div>
        </Card>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Spinner />
          </div>
        ) : errorMsg ? (
          <EmptyState
            card
            icon={Users}
            title="Couldn't load staff"
            description={errorMsg}
            action={<Button onClick={fetchList} variant="secondary">Try again</Button>}
          />
        ) : list.length === 0 ? (
          <EmptyState
            card
            icon={Users}
            title="No staff found"
            description="Add your ground staff to manage roles, attendance and salary."
            action={(
              <Button as={Link} to="/dashboard/staff/new">
                <Plus className="h-5 w-5" />
                Add Staff
              </Button>
            )}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {list.map((row) => {
              const isActive = (row.status || '').toUpperCase() === 'ACTIVE';
              return (
                <Card key={row.id} padding="none" className="flex flex-col overflow-hidden">
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <Link
                        to={`/dashboard/staff/${row.id}`}
                        className="text-base font-semibold text-ga-text-primary hover:text-ga-primary truncate block"
                      >
                        {row.name}
                      </Link>
                      <p className="text-sm text-ga-text-muted mt-0.5">
                        {ROLE_LABELS[(row.role || '').toUpperCase()] || row.role}
                      </p>
                    </div>
                    <Badge variant={isActive ? 'success' : 'neutral'}>{row.status}</Badge>
                  </div>

                  <div className="px-4 pb-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-ga-text-muted">
                      <Phone className="h-4 w-4 shrink-0 text-ga-text-subtle" aria-hidden />
                      <span className="truncate">{row.phone || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ga-text-muted">
                      <CalendarDays className="h-4 w-4 shrink-0 text-ga-text-subtle" aria-hidden />
                      <span>{row.joining_date ? format(new Date(row.joining_date), 'dd MMM yyyy') : '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ga-text-muted">
                      <Wallet className="h-4 w-4 shrink-0 text-ga-text-subtle" aria-hidden />
                      <span className="capitalize">{(row.salary_type || '').toLowerCase() || '—'}</span>
                    </div>
                  </div>

                  <div className="mt-auto flex items-center gap-2 border-t border-ga-border-subtle px-4 py-3">
                    <Link
                      to={`/dashboard/staff/${row.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-ga-primary hover:underline"
                    >
                      <Eye className="h-4 w-4" aria-hidden />
                      View
                    </Link>
                    <Link
                      to={`/dashboard/staff/${row.id}/edit`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-ga-primary hover:underline"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleStatus(row)}
                      className={`ml-auto text-sm font-medium ${isActive ? 'text-ga-danger hover:opacity-80' : 'text-ga-success hover:opacity-80'}`}
                    >
                      {isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
